import React, { useEffect, useRef, useState, useCallback } from "react";
import "./MonitorStaff.css";

const MonitorStaff = ({ 
  selectedStaff, 
  stopCall, 
  remoteStream, 
  sendToggleVideo,
  startRecording, 
  stopRecording, 
  recording, 
  callState, 
  networkStats 
}) => {
  const videoRef = useRef(null);
  const boxRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  // ----- STATE -----
  // Start centered-ish or at specific coordinates
  const [pos, setPos] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [videoOn, setVideoOn] = useState(true);
  const [audioOn, setAudioOn] = useState(false); 
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("Idle");
  const [debugInfo, setDebugInfo] = useState({ bitrate: 0, res: "0x0" });

  // Sync internal state with prop
  useEffect(() => { setConnectionStatus(callState); }, [callState]);

  // ----- DEBUG STATS -----
  useEffect(() => {
    if (!networkStats || !videoRef.current) return;
    setDebugInfo({
        bitrate: networkStats.bitrate || 0,
        res: `${videoRef.current.videoWidth}x${videoRef.current.videoHeight}`
    });
  }, [networkStats]);

  // ----- STREAM HANDLING -----
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (remoteStream && remoteStream.active) {
        // Prevent unnecessary re-assignments (fixes flickering)
        if (videoEl.srcObject !== remoteStream) {
            console.log("▶️ Assigning new stream");
            videoEl.srcObject = remoteStream;
        }
        
        // Handle Autoplay Logic
        if (videoEl.paused) {
            // Browsers block autoplay with audio. Mute first, play, then unmute if allowed.
            videoEl.muted = true; 
            
            videoEl.play().then(() => {
                setConnectionStatus("Live");
                // Sync Audio State after play starts
                videoEl.muted = !audioOn; 
            }).catch(e => {
                console.error("❌ Autoplay Blocked:", e);
                setConnectionStatus("Click to Play");
            });
        } else {
             // If already playing, ensure mute state matches React state
             videoEl.muted = !audioOn;
        }
    } else {
        videoEl.srcObject = null;
    }
  }, [remoteStream, audioOn]);

  // ----- DRAGGABLE LOGIC -----
  const startDrag = (e) => {
    // Disable drag in fullscreen
    if (isFullscreen) return; 
    
    setIsDragging(true);
    if (boxRef.current) {
        const rect = boxRef.current.getBoundingClientRect();
        dragOffset.current = { 
            x: e.clientX - rect.left, 
            y: e.clientY - rect.top 
        };
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      // Calculate new position
      let newX = e.clientX - dragOffset.current.x;
      let newY = e.clientY - dragOffset.current.y;

      // Boundary Checks (Keep inside window)
      const winWidth = window.innerWidth;
      const winHeight = window.innerHeight;
      const boxWidth = 300 * scale; // Approx width
      const boxHeight = 225 * scale; // Approx height

      // Clamp values
      if (newX < 0) newX = 0;
      if (newY < 0) newY = 0;
      if (newX + boxWidth > winWidth) newX = winWidth - boxWidth;
      if (newY + boxHeight > winHeight) newY = winHeight - boxHeight;

      setPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, scale]);

  // ----- HANDLERS -----
 const toggleFullscreen = () => {
   if (!document.fullscreenElement) {
       boxRef.current.requestFullscreen().catch(err => console.log(err));
       setIsFullscreen(true);
   } else {
       document.exitFullscreen();
       setIsFullscreen(false);
   }
};
  
const handleToggleVideo = () => {
      // 1. Calculate new state (Inverse of current)
      const newVideoState = !videoOn;
      
      // 2. Update UI Button
      setVideoOn(newVideoState);
      
      // 3. Send Signal to the Broadcaster
      if (sendToggleVideo) {
          console.log("signal sending:", newVideoState);
          sendToggleVideo(newVideoState); 
      } else {
          console.warn("sendToggleVideo function is missing!");
      }
  };

  const handleToggleAudio = () => {
    setAudioOn(prev => {
        const newState = !prev;
        if (videoRef.current) {
            videoRef.current.muted = !newState; 
        }
        return newState;
    });
  };

  const toggleVideoRecording = () => { 
    if (recording === "video") stopRecording(); 
    else startRecording("video"); 
  };

  // Determine styles dynamically
  const boxStyle = isFullscreen 
    ? {
        left: 0,
        top: 0,
        transform: "none", // Reset scale in fullscreen
        width: "100vw",
        height: "100vh",
        border: "none"
      }
    : {
        left: pos.x, 
        top: pos.y, 
        transform: `scale(${scale})`,
        width: "300px", 
        height: "225px",
        border: "2px solid #333"
      };

  // Hide component if idle/no stream
  if (callState === "IDLE" && !remoteStream) return null;

  return (
    <div
      ref={boxRef}
      className="monitorStaffBox"
      style={{
        ...boxStyle,
        backgroundColor: "#000", 
        position: "fixed",
        zIndex: 9999,
        boxShadow: isDragging ? "0 10px 20px rgba(0,255,0,0.2)" : "0 4px 10px rgba(0,0,0,0.5)",
        cursor: "default",
        borderRadius: isFullscreen ? "0" : "8px",
        overflow: "hidden"
      }}
    >
      {/* HEADER */}
      <div 
        onMouseDown={startDrag} 
        style={{ 
            position: 'absolute', top: 0, left: 0, right: 0, 
            background: 'rgba(0,0,0,0.7)', color: 'white', 
            padding: '6px 10px', fontSize: '12px', zIndex: 20, 
            display:'flex', justifyContent:'space-between', alignItems: 'center',
            cursor: isFullscreen ? 'default' : 'grab',
            userSelect: 'none',
            backdropFilter: 'blur(4px)'
        }}
      >
          <div style={{display:'flex', gap:'5px', alignItems:'center'}}>
            <span style={{width:'8px', height:'8px', borderRadius:'50%', background: connectionStatus === "Live" ? '#0f0' : '#fa0'}}></span>
            <span>{selectedStaff?.username || "Monitor"}</span>
          </div>
          
          <span style={{ fontFamily:'monospace', fontSize:'10px', color: debugInfo.bitrate > 500 ? '#0f0' : '#fa0' }}>
              {debugInfo.res} | {debugInfo.bitrate} kbps
          </span>
      </div>

      {/* VIDEO PLAYER */}
      <video 
        ref={videoRef} 
        autoPlay 
        playsInline 
        // Note: muted prop is handled via useEffect/ref for reliability
        style={{ width: "100%", height: "100%", objectFit: "cover", background: "#111" }} 
      />

      {/* CONTROLS */}
      <div className="controls" style={{
          display: 'flex', justifyContent: 'space-evenly', padding: '10px 5px', 
          background: 'rgba(0,0,0,0.8)', position: 'absolute', bottom: 0, width: '100%', zIndex: 20,
          backdropFilter: 'blur(4px)'
      }}>
        <ControlButton 
            onClick={stopCall} 
            color="white" bg="#e74c3c" label="End" icon="📞" 
        />
        <ControlButton 
            onClick={handleToggleVideo} 
            color="white" bg={videoOn ? "#555" : "#e74c3c"} label="Cam" icon={videoOn ? "📷" : "❌"} 
        />
        <ControlButton 
            onClick={handleToggleAudio} 
            color="white" bg={audioOn ? "#555" : "#e74c3c"} label="Mic" icon={audioOn ? "🔊" : "🔇"} 
        />
        <ControlButton 
            onClick={toggleVideoRecording} 
            color="white" bg={recording === "video" ? "#e74c3c" : "#555"} label="Rec" icon={recording === "video" ? "⏹" : "⏺"} 
        />
        <ControlButton 
            onClick={toggleFullscreen} 
            color="white" bg="#555" label="Full" icon={isFullscreen ? "↙️" : "↗️"} 
        />
      </div>
    </div>
  );
};

// Helper component for cleaner JSX
const ControlButton = ({ onClick, bg, color, icon }) => (
    <button onClick={onClick} style={{
        background: bg, color: color, border:'none', 
        borderRadius:'50%', width:'35px', height:'35px', 
        display:'flex', alignItems:'center', justifyContent:'center', 
        cursor:'pointer', fontSize:'16px', transition: 'all 0.2s'
    }}>
        {icon}
    </button>
);

export default MonitorStaff;